




import { computed, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import { isBefore } from "date-fns";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { Appointment, AppointmentStatus } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { AppointmentTypeKind } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { APPOINTMENT_OFFLINE_REMIND_MS, APPOINTMENT_ONLINE_REMIND_MS, EVENTS } from "@/const";
import AppointmentReminderView from "./components/AppointmentReminderView.vue";

export default {
  name: "AppointmentReminder",
  components: { AppointmentReminderView },
  setup() {
    const { authUser, isCreator } = useAuthUser();
    const appointmentsData = ref<Appointment[]>([]);
    const activeAppointment = ref<Appointment | null>(null);
    const forceHide = ref(false);
    const startTime = ref<number | null>(null);
    const appointmentDuration = ref<number | null>(null);
    const timer = ref<NodeJS.Timeout>();

    const show = computed(() => !forceHide.value && !!activeAppointment.value);
    const isOfflineAppointment = computed(() => activeAppointment.value?.typeKind === AppointmentTypeKind.TOUR_GUIDE);
    const userData = computed(() =>
      isCreator.value ? activeAppointment.value?.consumer : activeAppointment.value?.provider
    );

    const setForceHide = (status?: boolean) => {
      status !== undefined && (forceHide.value = status);
    };

    const clearAppointments = () => {
      activeAppointment.value = null;
      startTime.value = null;
    };

    const appointmentReminderController = () => {
      if (timer.value) {
        clearTimeout(timer.value);
      }
      if (!appointmentsData.value.length) {
        clearAppointments();
        return;
      }
      activeAppointment.value = appointmentsData.value[0];
      startTime.value = new Date(activeAppointment.value.startTime).getTime();
      const endTime = new Date(activeAppointment.value.endTime).getTime();
      appointmentDuration.value = endTime - startTime.value;
      timer.value = setTimeout(() => {
        appointmentsData.value.shift();
        appointmentReminderController();
      }, endTime - Date.now());
    };

    const checkToNeedShowed = (appointment: Appointment) => {
      const nearestStartTime = new Date(appointment.startTime).getTime();
      const nearestEndTime = new Date(appointment.endTime).getTime();
      const nearestAppointmentDuration = nearestEndTime - nearestStartTime;

      return (
        nearestEndTime - Date.now() <=
          (appointment.typeKind === AppointmentTypeKind.TOUR_GUIDE
            ? APPOINTMENT_OFFLINE_REMIND_MS
            : APPOINTMENT_ONLINE_REMIND_MS) +
            nearestAppointmentDuration && !appointmentsData.value.some(item => item.id === appointment.id)
      );
    };

    const getAppointments = async () => {
      if (!authUser.value) {
        clearAppointments();
        return;
      }
      const response = await providers.AppointmentProvider.search({
        sort: "START_TIME,ASC",
        status: [AppointmentStatus.APPROVED],
        endTimeFrom: new Date().toISOString(),
        ...(isCreator.value ? { providerId: authUser.value.id } : { consumerId: authUser.value.id }),
      });
      if (!response.result.length) {
        clearAppointments();
        return;
      }
      response.result.forEach((appointment: Appointment) => {
        if (checkToNeedShowed(appointment)) {
          appointmentsData.value.push(appointment);
        }
      });
      appointmentReminderController();
    };

    const pushAppointmentReminder = (notification?: Notification) => {
      if (!notification) return;
      if (!appointmentsData.value.some(item => item.id === notification.details.appointmentId)) {
        if (checkToNeedShowed(notification.details.appointment)) {
          appointmentsData.value.push(notification.details.appointment);
        }
        appointmentsData.value = appointmentsData.value.sort((i1, i2) =>
          isBefore(new Date(i1.startTime), new Date(i2.startTime)) ? -1 : 1
        );
      }
      appointmentReminderController();
    };

    watch([() => authUser.value], () => getAppointments(), { immediate: true });

    emitter.on<boolean>(EVENTS.MAIN_MENU_HIDE, setForceHide);
    emitter.on<Notification>(EVENTS.UPCOMING_APPOINTMENT_REMINDER, pushAppointmentReminder);
    onBeforeUnmount(() => {
      emitter.off<Notification>(EVENTS.UPCOMING_APPOINTMENT_REMINDER, pushAppointmentReminder);
      emitter.off<boolean>(EVENTS.MAIN_MENU_HIDE, setForceHide);
    });
    return {
      show,
      isOfflineAppointment,
      userData,
      startTime,
      appointmentsData,
    };
  },
};
